import React from 'react';
import ApolloClient from 'apollo-boost';
import { ApolloProvider } from '@apollo/react-hooks';
import Layout from '../../components/layout';
import SEO from '../../components/seo';
import { SettingProvider } from '../../lib/context/settings';
import { AdsProvider } from '../../lib/context/ads';
import { Button, Card, Col, Row } from 'antd';
import { device } from '../../lib/theme';
import styled from 'styled-components';
import {
  SecondarySectionTitle,
  SecondaryTitle,
  Title,
} from '../../components/styles';
import PromoSection from '../../components/sections/promo';
import Meta from 'antd/lib/card/Meta';

const client = new ApolloClient<any>({
  uri: `https://${process.env.GATSBY_WORDPRESS_URL_PATH}/graphql`,
  fetchOptions: {
    credentials: 'include',
  },
});

const HeroContainer = styled(Row)`
  background-image: url('https://aaacdndev.blob.core.windows.net/wordpress/2021/02/AAAKidsHeader.png');
  background-size: cover;
  background-position: right;
  background-repeat: no-repeat;
  height: 340px;

  h1 {
    max-width: 80%;
    font-size: 32px;
    line-height: 1.1;
  }

  @media ${device.laptop} {
    h1 {
      max-width: 60%;
      font-size: 56px;
      line-height: 1.2;
    }
  }
`;

const SafetyPatrolImage = styled(Col)`
  background-image: url('https://aaacdndev.blob.core.windows.net/wordpress/traffic-safety/safetyPatrol.png');
  background-size: cover;
  background-repeat: no-repeat;
  min-height: 300px;

  @media ${device.laptop} {
    min-height: 0;
  }
  
  }
`;

const PromoSectionContainer = styled(Row)`
  padding: 32px 0;

  .ant-card-body {
    min-height: 120px;
    position: relative;
  }
  .ant-card-meta-title {
    margin-bottom: 0 !important;
  }
  .ant-card-meta-description {
    font-family: 'CG-regular', sans-serif;
    font-size: 16px;
    line-height: 20px;
    color: #2c2c2c;
    min-height: 140px;
  }

  .ant-card .promo-action {
    background: transparent;
    border: none;
    color: #cd2127;
    font-family: 'CG-bold', sans-serif;
    font-weight: 400;
    font-size: 16px;
    padding: 0;
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 24px;
  }

  .ant-card .promo-action > img {
    margin-left: 8px;
  }

  .ant-carousel {
    width: 100%;
  }

  @media ${device.tablet} {
    padding: 64px 0;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-flow: column nowrap;
  margin-top: 32px;

  .ant-btn {
    min-width: 250px;
    min-height: 50px;
    margin-bottom: 16px;
    margin-right: 16px;
  }

  @media ${device.tablet} {
    flex-flow: row nowrap;
    button {
      margin-bottom: 0;
      text-align: center;
    }
  }

  @media ${device.mobileL} {
    text-align: center;
    align-items: center;
  }

  @media only screen and (max-width: 768px) {
    button,
    a {
      width: 100%;
      margin: 0 0 16px !important;
    }
  }
`;

const kidsSocialCards = [
  {
    img:
      'https://aaacdndev.blob.core.windows.net/wordpress/traffic-safety/carSeatSafety.png',
    type: 'traffic_safety-auto-insurance',
    title: 'Car Seat Safety Guide',
    description:
      'Child safety seats can reduce the risk of injury by 82% compared to seat belts alone, but they must be installed properly.',
    linkText: '',
    url: 'http://safeseats4kids.aaa.com/car-seat-guide/',
  },
  {
    img:
      'https://aaacdndev.blob.core.windows.net/wordpress/traffic-safety/heatStroke.png',
    type: 'traffic_safety-auto-buying',
    title: 'Heat Stroke Prevention',
    description:
      'More than half of heat stroke deaths occur when a caregiver forgot a child was in the vehicle. Get safety tips on how to prevent tragedy.',
    linkText: '',
    url: 'http://safeseats4kids.aaa.com/heatstroke-prevention/',
  },
  {
    img:
      'https://aaacdndev.blob.core.windows.net/wordpress/traffic-safety/backoverPrevention.png',
    type: 'traffic_safety-adults',
    title: 'Backover Prevention',
    description:
      'Safety experts advise education, supervision and vigilance to help keep children out of harm’s way and reduce risk of backover.',
    linkText: '',
    url: 'http://safeseats4kids.aaa.com/backover-prevention/',
  },
  {
    img:
      'https://aaacdndev.blob.core.windows.net/wordpress/traffic-safety/drivingClass.png',
    type: 'traffic_safety-backover',
    title: 'Driving Schools',
    description:
      'AAA Carolinas offers a variety of driver training solutions that promote safe and responsible driving.',
    linkText: '',
    url: '/traffic-safety/driving-schools/',
  },
];

const TeenPromoSingle: React.FC<{
  coverImage?: string;
  title?: string;
  description?: any;
  url?: string;
  linkText?: string;
}> = ({ coverImage, title, description, url, linkText }) => (
  <Col xs={{ span: 24 }} md={{ span: 8 }}>
    <Card
      hoverable
      cover={
        <div
          style={{
            backgroundImage: `url(${coverImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            paddingTop: '45%',
          }}
        />
      }
    >
      <Meta
        title={
          title ? (
            <SecondarySectionTitle marginBottom={16}>
              {title}
            </SecondarySectionTitle>
          ) : null
        }
        description={description}
      />
      <a className="promo-action" href={url}>
        {linkText ? linkText : 'Learn more'}
        <img src="/img/icon/right-arrow.png" />
      </a>
    </Card>
  </Col>
);

const AAAForTeens: React.FC<any> = () => {
  return (
    <ApolloProvider client={client as any}>
      <SettingProvider>
        <AdsProvider>
          <Layout key={`${typeof window}`}>
            <SEO
              title="AAA For Kids"
              description=""
              canonicalUrl="/traffic-safety/kids"
            />
            <HeroContainer />
            <Row style={{ marginTop: 32 }}>
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <Title style={{ textAlign: 'center' }}>AAA For Kids</Title>
                <h1 style={{ marginTop: 32 }}>
                  When kids learn how to be safe near traffic, they’re more
                  likely to continue responsible traffic safety behavior as an
                  adult.
                </h1>
                <Row style={{ backgroundColor: '#E4F2FE' }}>
                  <SafetyPatrolImage xs={{ span: 24 }} md={{ span: 6 }} />
                  <Col
                    xs={{ span: 24 }}
                    md={{ span: 17, offset: 1 }}
                    style={{ padding: 32 }}
                  >
                    <SecondaryTitle>School Safety Patrol</SecondaryTitle>
                    <p>
                      Build and grow a AAA School Safety Patrol in your school.
                      Get our catalog, order patrol equipment, or find
                      guidelines and resources from our School Safety Patrol
                      website.
                    </p>
                    <Buttons>
                      <Button
                        href="https://member.acg.aaa.com/car/driving-safety/aaa-school-safety-patrol.html?zip=28105&devicecd=PC"
                        type="primary"
                      >
                        Learn More
                      </Button>
                      <Button
                        type="primary"
                        href="https://member.acg.aaa.com/content/dam/acg/pdfs/driving-safety/school-safety-patrol/patrol-new-school-forms/AAA-School-Safety-Patrol-Registration-Form.pdf"
                      >
                        Register Your School
                      </Button>
                    </Buttons>
                  </Col>
                </Row>
                <Row style={{ marginTop: 16 }}>
                  <Col xs={{ span: 24 }}>
                    <p>
                      From car seats to bike safety and beyond, AAA Carolinas
                      has safety, education and awareness resources for getting
                      children to school safely, helping them stay safe as
                      pedestrians, while taking the school bus, and into their
                      teens when they become new drivers.
                    </p>
                    <p>
                      Get more information on Child Passenger Safety on the{' '}
                      <a href="http://safeseats4kids.aaa.com/">
                        AAA SafeSeat4Kids website
                      </a>
                      .
                    </p>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row style={{ marginTop: 32 }}>
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <SecondaryTitle>
                  Join Us to Celebrate 100 Years of the AAA School Safety Patrol
                  Program!
                </SecondaryTitle>
                <Row>
                  <Col xs={{ span: 24 }} md={{ span: 12 }}>
                    <p>
                      Since 1920, the AAA School Safety Patrol Program has
                      provided a safer pedestrian environment and a wide
                      spectrum of educational opportunities for millions of
                      children. During the program’s long and distinguished
                      history, AAA has successfully helped the Patrol to thrive
                      and expand, ensuring safer streets for all school
                      children.
                    </p>
                    <p>
                      AAA School Safety Patrols play an important role in
                      helping young pedestrians learn and fulfill
                      responsibilities regarding traffic safety. Millions of
                      U.S. boys and girls have honorably served their classmates
                      since the program began, and interest in the program has
                      spread around the world. New Zealand, the Netherlands,
                      England, Germany and France, have emulated the AAA School
                      Safety Patrol Program — with similar positive results.
                    </p>
                    <p>
                      Today, the AAA School Safety Patrol program is the largest
                      school-based safety program in the world, with more than
                      679,000 Patrollers in 35,000 schools across North America
                      and 30 countries, providing a safer environment for child
                      pedestrians and offering a spectrum of educational
                      opportunities for schoolchildren.
                    </p>
                    <p>
                      AAA recognizes the AAA School Safety Patrol Program as an
                      outstanding school safety activity. In addition to
                      Patrollers who deserve special thanks for their efforts,
                      AAA commends school personnel who administer the programs
                      and law enforcement officials who contribute to the
                      success of programs in their communities. The most
                      effective Patrols come from a strong partnership between
                      AAA, schools, parent-teacher associations, law
                      enforcement, parents and the community.
                    </p>
                  </Col>
                  <Col xs={{ span: 24 }} md={{ span: 10, offset: 2 }}>
                    <img src="https://aaacdndev.blob.core.windows.net/wordpress/2021/03/100th_Anniversary_School_Safty_Patrol.png" />
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row style={{ backgroundColor: '#eee', marginTop: 16 }}>
              <Col xs={{ span: 24 }}>
                <PromoSection promos={kidsSocialCards} />
              </Col>
            </Row>
            <Row style={{ marginTop: 32 }}>
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <SecondaryTitle>School Safety Patrol Program</SecondaryTitle>
                <p>
                  The AAA School Safety Patrol program gives students in North
                  and South Carolina a chance to make a diﬀerence in their
                  school and community. Students learn leadership and
                  organizational skills, responsibility, and the importance of
                  being a good role model.
                </p>
                <p>
                  Starting your AAA School Safety Patrol program is easy. Visit
                  our School Safety Patrol site for policies and practices
                  necessary for a School Safety Patrol. Standard operating
                  procedures in all safety patrols are key for motorists and
                  pedestrians to know what to expect.
                </p>
              </Col>
            </Row>

            <Row style={{ marginBottom: 64 }}>
              <Col xs={{ span: 22, offset: 1 }} md={{ span: 20, offset: 2 }}>
                <Row>
                  <Col>
                    <PromoSectionContainer gutter={[16, 16]}>
                      <TeenPromoSingle
                        coverImage="https://aaacdndev.blob.core.windows.net/wordpress/traffic-safety/trafficSafetyNewsletter.png"
                        title="Traffic Safety Newsletter"
                        description="Subscribe to our newsletter for reliable traffic safety
                        information and valuable safe driving tips and
                        resources."
                        url="/traffic-safety/newsletter/"
                        linkText="Learn More"
                      />
                      <TeenPromoSingle
                        coverImage="https://aaacdndev.blob.core.windows.net/wordpress/traffic-safety/safeDriving.png"
                        title="Safe Driving Resouces"
                        description="AAA Carolinas is committed to educating drivers about
                        traffic safety on the road, in the schools and in the
                        community."
                        url="/traffic-safety/safe-driving-resources"
                        linkText="Learn More"
                      />
                      <TeenPromoSingle
                        coverImage="https://aaacdndev.blob.core.windows.net/wordpress/traffic-safety/AAATalks.png"
                        title="AAA Talks Traffic Safety"
                        description="Read more about AAA Traffic Safety’s efforts to educate
                        and promote safe driving habits in North and South
                        Carolina."
                        url="/go-magazine/expert-advice/traffic-safety"
                        linkText="Learn More"
                      />
                    </PromoSectionContainer>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Layout>
        </AdsProvider>
      </SettingProvider>
    </ApolloProvider>
  );
};

export default AAAForTeens;
